/**
 * 聊天相关API服务
 * 包含会话管理、消息发送等功能
 */

import { fetchWithSSE, SSEConnection, SSEConfig } from '@/api/base/sse-fetcher'
import type {
  ChatRequest,
  ChatResponse,
  ModelConfig,
  SearchConfig,
} from '@/api/types/chat.type'

/**
 * 聊天相关API路径
 */
export const CHAT_API_PATHS = {
  /** 与智能体对话 */
  AGENT_CHAT: '/agent',
} as const

/**
 * 默认模型配置
 */
export const DEFAULT_MODEL_CONFIG: ModelConfig = {
  model: 'qwen-plus',
  base_url:
    'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions',
  think: true,
}

/**
 * 聊天服务
 */
export const chatService = {
  /**
   * 与智能体对话（SSE方式）
   * @param request 聊天请求参数
   * @param config SSE配置
   * @returns SSE连接实例
   */
  chatWithAgentSSE: (
    request: ChatRequest,
    config: SSEConfig = {}
  ): SSEConnection => {
    const connection = new SSEConnection(config)
    connection.connect(CHAT_API_PATHS.AGENT_CHAT, request)
    return connection
  },

  /**
   * 与智能体对话（Promise方式）
   * @param request 聊天请求参数
   * @param config SSE配置
   * @returns Promise<void>
   */
  chatWithAgent: async (
    request: ChatRequest,
    config: SSEConfig = {}
  ): Promise<void> => {
    console.log('使用fetchWithSSE发送POST请求...')
    await fetchWithSSE(CHAT_API_PATHS.AGENT_CHAT, request, config)
  },

  /**
   * 创建聊天请求参数
   * @param sessionId 会话ID
   * @param agentId 智能体ID
   * @param content 用户输入内容
   * @param systemPrompt 系统提示词
   * @param searchConfig 搜索配置
   * @param modelConfig 模型配置（可选）
   * @param forcdRag 是否强制使用RAG（默认true）
   * @returns 聊天请求参数
   */
  createChatRequest: (
    sessionId: string,
    agentId: string,
    content: string,
    systemPrompt: string,
    searchConfig: SearchConfig,
    modelConfig: ModelConfig = DEFAULT_MODEL_CONFIG,
    forcdRag: boolean = true
  ): ChatRequest => {
    return {
      session_id: sessionId,
      agent_id: agentId,
      content,
      system_prompt: systemPrompt,
      model: modelConfig,
      force_rag: forcdRag,
      search_config: searchConfig,
    }
  },

  /**
   * 创建搜索配置
   * @param workspaceId 工作空间ID
   * @param knowledgeId 知识库ID
   * @param datasetId 数据集ID
   * @returns 搜索配置
   */
  createSearchConfig: (
    workspaceId: string,
    knowledgeId: string,
    datasetId: string
  ): SearchConfig => {
    return {
      db_name: `workspace_${workspaceId}`,
      milvus_config: [
        {
          collection: `knowledge_${knowledgeId}`,
          part: `dataset_${datasetId}`,
        },
      ],
    }
  },

  /**
   * 解析SSE响应数据
   * @param data SSE数据字符串
   * @returns 解析后的聊天响应
   */
  parseChatResponse: (data: string): ChatResponse | null => {
    try {
      return JSON.parse(data) as ChatResponse
    } catch (error) {
      console.error('解析聊天响应失败:', error)
      return null
    }
  },
}

/**
 * 聊天Hook类型
 */
export interface UseChatOptions {
  /** 会话ID */
  sessionId: string
  /** 智能体ID */
  agentId: string
  /** 系统提示词 */
  systemPrompt: string
  /** 工作空间ID */
  workspaceId: string
  /** 知识库ID */
  knowledgeId: string
  /** 数据集ID */
  datasetId: string
  /** 消息回调 */
  onMessage?: (response: ChatResponse) => void
  /** 错误回调 */
  onError?: (error: Event) => void
  /** 完成回调 */
  onDone?: () => void
  /** 超时时间（毫秒） */
  timeout?: number
}

/**
 * 使用聊天的Hook
 * @param options 聊天选项
 * @returns 聊天方法和状态
 */
export const useChat = (options: UseChatOptions) => {
  const {
    sessionId,
    agentId,
    systemPrompt,
    workspaceId,
    knowledgeId,
    datasetId,
    onMessage,
    onError,
    onDone,
    timeout = 30000,
  } = options

  /**
   * 发送消息
   * @param content 用户输入内容
   * @returns Promise<void>
   */
  const sendMessage = async (content: string): Promise<void> => {
    const searchConfig = chatService.createSearchConfig(
      workspaceId,
      knowledgeId,
      datasetId
    )

    const request = chatService.createChatRequest(
      sessionId,
      agentId,
      content,
      systemPrompt,
      searchConfig
    )

    const config: SSEConfig = {
      onMessage: (event) => {
        const response = chatService.parseChatResponse(event.data)
        if (response && onMessage) {
          onMessage(response)
        }
      },
      onError,
      onDone,
      timeout,
    }

    await chatService.chatWithAgent(request, config)
  }

  /**
   * 发送消息（SSE连接方式）
   * @param content 用户输入内容
   * @returns SSE连接实例
   */
  const sendMessageSSE = (content: string): SSEConnection => {
    const searchConfig = chatService.createSearchConfig(
      workspaceId,
      knowledgeId,
      datasetId
    )

    const request = chatService.createChatRequest(
      sessionId,
      agentId,
      content,
      systemPrompt,
      searchConfig
    )

    const config: SSEConfig = {
      onMessage: (event) => {
        const response = chatService.parseChatResponse(event.data)
        if (response && onMessage) {
          onMessage(response)
        }
      },
      onError,
      onDone,
      timeout,
    }

    return chatService.chatWithAgentSSE(request, config)
  }

  return {
    sendMessage,
    sendMessageSSE,
  }
}
